/**
 * SKE-4681 — the campaign banner.
 *
 * SHORT, DELIBERATELY. The headline first, then a quieter subhead under it
 * that the register supplies. Then the progress
 * row. From sm up the button and the dismiss sit to the right, centred against
 * that whole block; on phones they stack beneath it. Anything more is how this
 * becomes a paragraph nobody reads.
 *
 * It derives nothing. The week, whether dismissal is allowed and the snooze
 * duration all come from the status endpoint, because four surfaces reading one
 * answer is the entire design and a banner that shouted while the page stayed
 * calm would be worse than no banner.
 *
 * It vanishes the moment the last affected account is done: `isAffected` is
 * false once nothing is outstanding, without needing its own rule for it.
 */

import React, { useCallback, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import { getUser } from "../../selectors/commonSelectors";
import { Button } from "../../components/ui/button";
import { useTrackEvent } from "../../libs/analytics";
import { INSTAGRAM_CAMPAIGN_EVENTS } from "../../libs/analytics/instagram-campaign-events";
import { useBannerHeightProperty } from "../banner-stack/banner-offset";
import { useBannerSlotWithLiveState } from "../banner-stack/banner-precedence";
import { INSTAGRAM_CAMPAIGN_ROUTE, registerFor } from "./campaign-registers";
import type { CampaignCopyContext } from "./campaign-registers";
import { useCampaignSnooze } from "./use-campaign-snooze";
import type { CampaignSnooze } from "./use-campaign-snooze";
import { useCampaignStatus } from "./use-campaign-status";

export function InstagramCampaignBanner(): React.ReactElement | null {
  const navigate = useNavigate();
  const location = useLocation();
  const trackEvent = useTrackEvent();
  const user = useSelector(getUser);
  const { status, isAffected, outstanding, accounts, progress } =
    useCampaignStatus();

  const campaign = status?.campaign;
  const register = registerFor(campaign?.week);
  const { isSnoozed, snooze } = useCampaignSnooze(
    campaign?.snooze as CampaignSnooze | undefined,
    campaign?.snoozeMs
  );

  /*
   * The campaign page carries the same call to action in its own sticky rail,
   * and the spec allows exactly one primary button on screen. So the banner
   * stands down on the one route that already asks for the thing it is asking
   * for. Everywhere else it renders as normal; the sidebar entry stays put on
   * this route, because that is chrome and marks where the customer is.
   */
  const isOnCampaignPage = location.pathname.startsWith(
    INSTAGRAM_CAMPAIGN_ROUTE
  );

  const wantsToRender = isAffected && !isSnoozed && !isOnCampaignPage;
  const { mayRender, isBlockedNow } = useBannerSlotWithLiveState(
    "instagram-campaign",
    wantsToRender
  );
  // SKE-4881. Measured on the rendered root, so the sticky chrome below
  // knows how far the banner layer reaches.
  const bannerRef = useBannerHeightProperty();

  // `eligible` fires once per session rather than once per render, which is why
  // it lives in the browser at all: the status endpoint is polled by every
  // campaign surface, so firing it server-side would count one event per poll.
  const firedEligible = useRef(false);
  useEffect(() => {
    if (!isAffected || firedEligible.current) return;
    firedEligible.current = true;
    trackEvent({
      eventName: INSTAGRAM_CAMPAIGN_EVENTS.eligible,
      metadata: { week: campaign?.week, outstanding: outstanding.length },
    });
  }, [isAffected, campaign?.week, outstanding.length, trackEvent]);

  /*
   * `prompted` means a surface was actually SHOWN, so it is keyed to the
   * render decision and not to eligibility. A banner suppressed behind an
   * outage or a billing message was never shown and must not be counted.
   *
   * THE BLOCKED STATE IS RE-READ HERE, not taken from `mayRender`. The
   * precedence helper claims its slot in a layout effect, so nothing
   * overlapping is ever painted, but this passive effect still runs against
   * the snapshot taken during the render BEFORE that claim landed. Trusting
   * the captured value would latch `true` for a banner that is already losing
   * the slot, and the ref would keep it wrong for the rest of the session. A
   * funnel that counts prompts nobody saw is worse than one that counts none.
   *
   * Passive effects run after every layout effect of their commit, so by the
   * time this line executes the store is already correct. An earlier version
   * deferred to `requestAnimationFrame` instead, which also worked, but frames
   * do not fire in a background tab: a customer who opened Sked in a tab they
   * had not looked at yet would never have been counted at all.
   */
  const firedPrompted = useRef(false);
  useEffect(() => {
    if (!mayRender || isBlockedNow() || firedPrompted.current) return;
    firedPrompted.current = true;
    trackEvent({
      eventName: INSTAGRAM_CAMPAIGN_EVENTS.prompted,
      metadata: { week: campaign?.week, surface: "banner" },
    });
  }, [mayRender, isBlockedNow, campaign?.week, trackEvent]);

  const openCampaign = useCallback(() => {
    navigate(INSTAGRAM_CAMPAIGN_ROUTE);
  }, [navigate]);

  if (!mayRender || !campaign) {
    return null;
  }

  const done = accounts.length - outstanding.length;
  /*
   * FLOOR, not round, below completion. With 199 of 200 done, rounding
   * announces 100% to a screen reader while the sentence beside it still says
   * one account is outstanding, which is the reading most likely to make
   * someone stop. Only a genuinely finished set reports 100.
   */
  const percent = done === accounts.length ? 100 : Math.floor(progress * 100);

  const copyContext: CampaignCopyContext = {
    cutoverAt: campaign.cutoverAt,
    daysRemaining: campaign.daysRemaining,
    // The OUTSTANDING count, not the total: week 3 says how many
    // accounts will stop publishing, not how many exist.
    outstanding: outstanding.length,
    // The profile's zone, never the browser's. See the note on
    // `formatCutover`.
    timeZone: user?.timezone,
    isFinal72Hours: campaign.isFinal72Hours,
  };
  const subhead =
    typeof register.subhead === "function"
      ? register.subhead(copyContext)
      : register.subhead;

  return (
    <div
      ref={bannerRef}
      className={`sticky top-0 z-50 w-full border-b-2 px-6 py-4 ${register.wrapper}`}
      /*
       * A REGION, not a live region. `ServiceStatusBanner` uses
       * role="status" + aria-live, and rightly: an incident appearing
       * mid-session is news. This banner is persistent chrome that mounts on
       * every page load, so a live region would read the whole thing aloud on
       * each navigation, and again whenever a higher-priority banner clears and
       * this one takes the slot. It is a landmark the reader can move to.
       */
      role="region"
      aria-label="Instagram publishing update"
      data-testid="instagram-campaign-banner"
      data-week={campaign.week ?? undefined}
      data-final-72={campaign.isFinal72Hours ? "true" : "false"}
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
        <div className="flex min-w-0 flex-col gap-2 sm:flex-1">
          {/* Line one: the message. */}
          <div className="flex items-center gap-3">
            {register.flag ? (
              <span
                className="inline-flex items-center gap-2 rounded-md bg-white px-2 py-1 font-gotham text-xs font-semibold uppercase tracking-wide text-red-600"
                data-testid="instagram-campaign-flag"
              >
                <i
                  aria-hidden="true"
                  className="fa-regular fa-triangle-exclamation"
                />
                {register.flag}
              </span>
            ) : null}
            <span
              className={`font-gotham text-sm font-semibold ${register.title}`}
              data-testid="instagram-campaign-message"
            >
              {register.message(copyContext)}
            </span>
          </div>

          {/* Pulled up so it reads with line one, not the bar. */}
          {subhead ? (
            <p
              className={`-mt-1 font-inter text-sm font-normal ${register.body}`}
              data-testid="instagram-campaign-subhead"
            >
              {subhead}
            </p>
          ) : null}

          {/* The progress row. */}
          <div className="flex items-center gap-3">
            <div
              className={`h-1.5 w-40 overflow-hidden rounded-full ${register.progressTrack}`}
              role="progressbar"
              aria-valuenow={percent}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label="Instagram accounts updated"
              data-testid="instagram-campaign-progress"
            >
              <div
                className={`h-full rounded-full ${register.progressFill}`}
                style={{ width: `${percent}%` }}
              />
            </div>
            <span className={`font-inter text-sm ${register.body}`}>
              {done} of {accounts.length} updated
            </span>
          </div>
        </div>

        <div className="flex flex-shrink-0 items-center gap-2 self-start sm:self-auto">
          <Button
            // md, matching the three sibling banners. sm sat a row shorter.
            size="md"
            variant={
              register.buttonVariant === "white"
                ? "outline"
                : register.buttonVariant
            }
            /*
             * The `outline` variant's own hover and active states have to be
             * overridden explicitly, not just its resting colours. Left
             * alone, `active:bg-blue-50 active:text-blue-500` survives
             * tailwind-merge and flashes blurple on a red ground when
             * pressed, and `hover:border-neutral-100` greys the border.
             */
            className={
              register.buttonVariant === "white"
                ? "border-white bg-white text-red-700 hover:border-white hover:bg-white hover:text-red-700 active:border-white active:bg-white active:text-red-700"
                : undefined
            }
            onClick={openCampaign}
            data-testid="instagram-campaign-cta"
          >
            {register.buttonLabel}
          </Button>

          {campaign.dismissible ? (
            <button
              type="button"
              onClick={snooze}
              className={`rounded-lg p-2 transition-colors ${register.dismiss}`}
              aria-label="Dismiss Instagram publishing banner"
              data-testid="instagram-campaign-dismiss"
            >
              <i aria-hidden="true" className="fa-regular fa-times text-lg" />
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
